/**
 * forecast-buckets.ts — Forecast bucket logic + score-badge color.
 *
 * Canon §6 (2026-06-09) supersedes the W66.E May 31 module. Forecast bucket
 * labels are text-only. Color belongs to the SCORE BADGE only, keyed to the
 * same composite ranges. One canon hex per band.
 *
 * The color follows the number, not the bucket text. A green-scoring deal that
 * has been override-set to Pipeline still reads "Pipeline" in plain text — only
 * the score badge carries color.
 *
 * Canon 5-color palette (the only palette allowed):
 *   gray   #6B7280  unscored
 *   red    #DC2626  Omit, score < 30, or hard stop
 *   orange #EA580C  Pipeline, 30-49
 *   yellow #EAB308  Best Case, 50-64
 *   green  #16A34A  Upside 65-79 and Commit >= 80
 *
 * SINGLE SOURCE OF TRUTH for forecast bucket logic and the score-badge color.
 * Every consumer must import from this module. No local redefinitions.
 */

/** The 5 canonical forecast bucket labels. */
export type ForecastBucket =
  | "Commit"
  | "Upside"
  | "Best Case"
  | "Pipeline"
  | "Omit";

/**
 * Assign a forecast bucket from a composite score and hard-stop flag.
 *
 * @param composite     Composite score 0-100. NULL → treat as 0.
 * @param hasHardStops  True if any hard stop is active on this deal.
 *
 * Priority:
 *   1. hasHardStops || composite < 30  → "Omit"
 *   2. composite >= 80                 → "Commit"
 *   3. composite >= 65                 → "Upside"
 *   4. composite >= 50                 → "Best Case"
 *   5. else                            → "Pipeline"
 */
export function bucketForScore(
  composite: number | null | undefined,
  hasHardStops: boolean = false,
): ForecastBucket {
  const score = composite ?? 0;
  if (hasHardStops || score < 30) return "Omit";
  if (score >= 80) return "Commit";
  if (score >= 65) return "Upside";
  if (score >= 50) return "Best Case";
  return "Pipeline";
}

/**
 * Canon score-band colors, keyed to the forecast bucket the composite falls in.
 * The score follows the number: ranges that map to the same canon color share
 * one hex. This colors the SCORE BADGE only — never the bucket label.
 */
export const SCORE_BAND_COLORS: Record<ForecastBucket, string> = {
  "Commit":    "#16A34A",
  "Upside":    "#16A34A",
  "Best Case": "#EAB308",
  "Pipeline":  "#EA580C",
  "Omit":      "#DC2626",
};

/**
 * Canon hex for the score badge, keyed to the composite via the same bands as
 * bucketForScore. Returns gray (#6B7280) when unscored — null/undefined
 * composite and no hard stop. A hard stop with no score still reads red, since
 * a hard stop is a hard Omit.
 */
export function scoreBadgeColor(
  composite: number | null | undefined,
  hasHardStops: boolean = false,
): string {
  if ((composite === null || composite === undefined) && !hasHardStops) {
    return "#6B7280";
  }
  return SCORE_BAND_COLORS[bucketForScore(composite, hasHardStops)];
}

/** Display order for forecast buckets — score descending, Omit last. */
export const BUCKET_ORDER: ForecastBucket[] = [
  "Commit",
  "Upside",
  "Best Case",
  "Pipeline",
  "Omit",
];
